import { useEffect, useMemo, useState } from 'react';
import type { ElementNodeDto, ElementType } from '@xshop/shared';
import { elementMarkAttrs, type ElementMarkAttrs } from './editorElement';

// The marks the canvas reads a page's tree back off, built from the catalog the builder already owns.
//
// The catalog is seventy definitions of default props, styles and setting fields: control-plane data,
// and far too much of it to put in the bundle a shopper downloads in order to spell two attributes
// nobody on their page will ever read. So it is imported only once a builder is actually open, and a
// node drawn before it lands still wears its id and its type — which is everything selection needs.

/** The two names a merchant's own words are stored under across the whole catalog. */
const TEXT_PROPS: readonly string[] = ['text', 'label'];

/** What this hook needs out of the catalog module, named so the dynamic import is not an untyped blob. */
type MarkCatalog = {
  elementDefinition: (type: ElementType) => { accepts: readonly ElementType[]; defaults: { props: Record<string, unknown> } };
};

// Names the prop a double-click types into: the one the catalog's own defaults carry, or nothing.
// A prop the shop is answering is not one of them — typing over a bound heading writes words the next
// page load throws away, which reads as an editor that lost the edit.
const textPropOf = (node: ElementNodeDto, defaults: Record<string, unknown>): string | null => {
  const key = TEXT_PROPS.find((name) => typeof defaults[name] === 'string');
  if (key === undefined || node.bindings?.[key] !== undefined) return null;
  return key;
};

// Serves the mark builder once the catalog has arrived, and null everywhere a builder is not open.
export function useElementMarks(editing: boolean): ((node: ElementNodeDto) => ElementMarkAttrs) | null {
  const [catalog, setCatalog] = useState<MarkCatalog | null>(null);

  useEffect(() => {
    if (!editing) return undefined;
    let cancelled = false;
    import('../elements/elementCatalog')
      .then((module) => { if (!cancelled) setCatalog(module as MarkCatalog); })
      .catch((error: Error) => console.warn('Element catalog unavailable:', error.message));
    return () => { cancelled = true; };
  }, [editing]);

  // Held steady across renders: the walk takes this as wiring, and a new function every render would
  // hand the whole tree a new context value and redraw the page on every keystroke.
  return useMemo(() => {
    if (!catalog) return null;
    return (node: ElementNodeDto): ElementMarkAttrs => {
      const definition = catalog.elementDefinition(node.type);
      return elementMarkAttrs(node, definition.accepts, textPropOf(node, definition.defaults.props));
    };
  }, [catalog]);
}
